/**
 * Forgot Password Modal Component
 * 
 * Modal form for password reset with email validation.
 * Integrates with the authentication system and provides user feedback.
 */

import React, { useState, FormEvent, useEffect } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import Toast from '../common/Toast';
import { apiService } from '../../services/api';
import './ForgotPasswordModal.css';

// Type definitions
interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (response: any) => void;
}

interface FormData {
  email: string;
}

interface FormErrors {
  email?: string;
  general?: string;
}

/**
 * Forgot Password Modal component
 */
const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showToast, setShowToast] = useState<boolean>(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
      });
      setErrors({});
      setSuccessMessage('');
      setShowToast(false);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  /**
   * Handle input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific errors when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: undefined,
      }));
    }

    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.post('/auth/reset_password', {
        email: formData.email,
      });

      // Show success message
      const message = 'If an account with this email exists, a reset link has been sent.';
      setSuccessMessage(message);
      setShowToast(true);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error: any) {
      // Always show the same message for security reasons
      const message = 'If an account with this email exists, a reset link has been sent.';
      setSuccessMessage(message);
      setShowToast(true);

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle backdrop click to close modal
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div className="forgot-password-modal-backdrop" onClick={handleBackdropClick}>
          <div className="forgot-password-modal" role="dialog" aria-labelledby="forgot-password-modal-title" aria-modal="true">
            <div className="forgot-password-modal__header">
              <h2 id="forgot-password-modal-title" className="forgot-password-modal__title">Reset Password</h2>
              <button
                type="button"
                className="forgot-password-modal__close"
                onClick={onClose}
                aria-label="Close forgot password modal"
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form className="forgot-password-modal__form" onSubmit={handleSubmit} noValidate>
              {/* Success Message */}
              {successMessage && (
                <div className="forgot-password-modal__success" role="alert">
                  <svg className="forgot-password-modal__success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                  {successMessage}
                </div>
              )}

              {/* Error Display */}
              {errors.general && (
                <div className="forgot-password-modal__error" role="alert">
                  <svg className="forgot-password-modal__error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {typeof errors.general === 'string' ? errors.general : JSON.stringify(errors.general)}
                </div>
              )}

              {/* Instructions */}
              <div className="forgot-password-modal__instructions">
                <p>Enter your email and we'll send you reset link.</p>
              </div>

              {/* Form Fields */}
              <div className="forgot-password-modal__fields">
                {/* Email Field */}
                <div className="forgot-password-modal__field">
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="forgot-password-modal__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isLoading}
                  className="forgot-password-modal__cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading || !!successMessage}
                  className="forgot-password-modal__submit-button"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showToast && (
        <Toast
          message={successMessage}
          duration={5000}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};

export default ForgotPasswordModal;
